// Pure candle math for the live pair chart — no React, no IO.
//
// The wt-api OHLCV endpoint returns CONTINUOUS candles (CHART-FINAL): the
// backend rewrites each bar's open to the previous bar's close at read time
// (backend/pkg/api/ohlcv/repository.go::applyContinuousOpen) so a bucket
// holding a single trade still draws a body and a down-close bar is red.
// mergeTradeIntoCandles mirrors that rule so a WS-merged candle converges to
// what the next REST fetch returns:
//   - a freshly opened bucket opens at the PREVIOUS candle's close (the very
//     first candle of the series has no predecessor and opens at its own
//     first trade); h/l are clamped to enclose that open;
//   - within a bucket: h = max(price), l = min(price), c = last(price),
//     v = sum(quote_amount), tx_count = count(), buckets aligned on UTC
//     epoch boundaries (SAMPLE BY convention).
// A late out-of-order trade folded into (or inserted before) an earlier bucket
// changes that bucket's close, and the next bucket's open IS that close, so the
// continuous-open rule is re-applied forward until it stops changing anything.
// Without that the live series drifted away from the REST/MV snapshot for as
// long as the tab stayed open, and a refresh visibly "corrected" the chart.

import type { Candle, Resolution } from '../api/types'

// Must stay in lock-step with backend/pkg/api/ohlcv/types.go::BucketSeconds.
const BUCKET_SECONDS: Record<Resolution, number> = {
  '1s': 1,
  '5s': 5,
  '15s': 15,
  '30s': 30,
  '1m': 60,
  '5m': 300,
  '15m': 900,
  '30m': 1800,
  '1h': 3600,
  '4h': 14400,
  '1d': 86400,
}

export function bucketSeconds(res: Resolution): number {
  return BUCKET_SECONDS[res] ?? 60
}

export function bucketStart(tsSec: number, res: Resolution): number {
  const step = bucketSeconds(res)
  return Math.floor(tsSec / step) * step
}

/** The subset of a WS trade event the candle merge needs. */
export interface CandleTrade {
  ts: number // Unix seconds
  price: number
  quote_amount: number
}

// candleTradePrice picks the price a live WS trade contributes to a candle. The event's
// spot_price field now carries the backend's ROBUST candle price (EXECUTION-CANDLES,
// block_pull candleRobustPrice): the trade EXECUTION price for real trades, the pool spot
// only for DUST (tiny rounding-artifact fills). We use it directly (it is always > 0),
// falling back to price. This mirrors the OHLCV MV's candleRobustExpr so a WS-merged candle
// converges to what the next REST fetch returns — no spot->execution jump on refresh, and
// real price-impact shows live (LIVE-CANDLE-SPOT -> EXECUTION-CANDLES). The trade feed and
// volume keep raw execution price. Pure.
export function candleTradePrice(t: { price: number; spot_price?: number | null }): number {
  return typeof t.spot_price === 'number' && t.spot_price > 0 ? t.spot_price : t.price
}

// tradeInUSD denominates a live WS trade in USD for the USD chart toggle
// (FRONTEND-USD-TOGGLE). It multiplies by the CURRENT quote→USD rate (tonUsdRate)
// rather than a per-bucket historical rate: a live tick always lands in the open
// ("now") bucket, whose per-bucket rate IS the current rate — so the merged
// candle stays consistent with the backend's USD seed (?denom=usd), which
// converted history per-bucket. Returns the trade unchanged (same reference,
// no allocation) when the rate is unusable — TON mode passes rate=null, and a
// non-positive/NaN rate must never zero out the tick. Pure.
export function tradeInUSD(trade: CandleTrade, rate: number | null): CandleTrade {
  if (rate == null || !(rate > 0)) return trade
  return { ts: trade.ts, price: trade.price * rate, quote_amount: trade.quote_amount * rate }
}

// mergeTradeIntoCandles returns a NEW array with the trade folded into
// its bucket; `candles` is returned unchanged when the trade can't be
// placed. Identity of untouched elements is preserved so the chart
// layer can detect tail-only updates and avoid a full setData reset.
//
// Placement rules:
//   - bucket at/after the last candle: open-candle merge / append.
//     Appending NEVER synthesizes filler candles for skipped buckets —
//     a candle is a trade (CHART-27.2 founder rule); empty buckets
//     simply don't exist and lightweight-charts renders the real
//     candles adjacent, wils.fun style.
//   - bucket BEHIND the last candle but present in the array: merge in
//     place — WS trades carry the on-chain ts and can arrive after a
//     newer trade already opened a later bucket.
//   - bucket BEHIND the last candle and absent: inserted at its sorted
//     position. WS delivery is ~2s behind the on-chain ts, so on sub-minute
//     resolutions this is the COMMON case, not an edge case, and the series
//     is genuinely sparse. Dropping it made the live right edge lose prints
//     the longer the page stayed open while the seeded left side stayed
//     correct — the chart looked fine on entry and decayed from there.
//   - bucket before the first candle: dropped; that history is owned by
//     pagination (prependOlderCandles) and the next refetch reconciles.
export function mergeTradeIntoCandles(
  candles: Candle[],
  trade: CandleTrade,
  res: Resolution
): Candle[] {
  if (!Number.isFinite(trade.price) || trade.price <= 0) return candles
  const bucket = bucketStart(trade.ts, res)
  if (
    candles.length === 1 &&
    candles[0]?.provenance === 'pool_birth' &&
    candles[0].tx_count === 0
  ) {
    return [firstTradeFromBirth(candles[0], trade, bucket, bucketSeconds(res))]
  }
  const last = candles.length > 0 ? candles[candles.length - 1] : null

  if (!last || bucket > last.t) {
    // Continuous open (CHART-FINAL): inherit the previous candle's close so
    // a single-trade bucket renders a body, not a bodiless dash. The first
    // candle of the series opens at its own trade. Clamp h/l to enclose the
    // open so the body never escapes its wick.
    const open = last ? last.c : trade.price
    const opened: Candle = {
      t: bucket,
      o: open,
      h: Math.max(open, trade.price),
      l: Math.min(open, trade.price),
      c: trade.price,
      v: Math.max(0, trade.quote_amount),
      tx_count: 1,
    }
    return [...candles, opened]
  }

  const i = indexOfBucket(candles, bucket)
  if (i < 0) {
    // The bucket does not exist yet. Dropping the trade here is what made the
    // chart decay the longer the page stayed open: WS events carry the on-chain
    // ts but arrive ~2s later, so on sub-minute resolutions a live trade lands
    // BEHIND the last candle, and the series is genuinely sparse (a real 1s
    // seed runs 1785007768, 771, 774, 775), so its bucket is usually absent.
    // Insert it in place instead — the seeded left side was never the problem.
    // A bucket older than the whole series is NOT ours: that history belongs to
    // pagination (prependOlderCandles), and inventing a bar before the loaded
    // window's left edge would show data the window never claimed to cover.
    const first = candles[0]
    if (first && bucket < first.t) return candles
    const at = insertionIndex(candles, bucket)
    const prev = at > 0 ? (candles[at - 1] as Candle) : null
    const open = prev ? prev.c : trade.price
    const out = candles.slice()
    out.splice(at, 0, {
      t: bucket,
      o: open,
      h: Math.max(open, trade.price),
      l: Math.min(open, trade.price),
      c: trade.price,
      v: Math.max(0, trade.quote_amount),
      tx_count: 1,
    })
    return reanchorContinuousOpenFrom(out, at + 1)
  }

  const target = candles[i] as Candle
  const out = candles.slice()
  out[i] = mergeIntoBucket(target, trade)
  return reanchorContinuousOpenFrom(out, i + 1)
}

// reanchorContinuousOpenFrom re-applies the CHART-FINAL continuous-open rule
// forward from `start`, mirroring the backend's read-time applyContinuousOpen.
//
// Touching an earlier bucket changes its close, and the next bucket's open IS
// that close. Leaving it stale made the live series drift away from what the
// REST fetch returns, so a page left open slowly diverged and a refresh
// visibly "corrected" it. WS delivery runs ~2s behind the on-chain ts, so on
// sub-minute resolutions a late merge is the common case, not an edge one, and
// the drift accumulates for as long as the tab stays open.
//
// It stops at the first bar already consistent with its predecessor: opens only
// propagate while they actually change, so the usual tail-only edit rewrites one
// or two bars and every earlier bar keeps its object identity for the chart's
// tail-update fast path. A full rewrite falls back to setData, whose visible
// range is restored by preservingView, so the user's zoom survives either way.
function reanchorContinuousOpenFrom(candles: Candle[], start: number): Candle[] {
  for (let i = Math.max(1, start); i < candles.length; i++) {
    const prev = candles[i - 1] as Candle
    const bar = candles[i] as Candle
    const open = prev.c
    const high = Math.max(bar.h, open)
    const low = Math.min(bar.l, open)
    if (bar.o === open && bar.h === high && bar.l === low) return candles
    candles[i] = { ...bar, o: open, h: high, l: low }
  }
  return candles
}

// insertionIndex is the position where `t` keeps the (ASC, bucket-aligned)
// array sorted — the first index whose candle is newer than t.
function insertionIndex(candles: Candle[], t: number): number {
  let lo = 0
  let hi = candles.length
  while (lo < hi) {
    const mid = (lo + hi) >> 1
    if ((candles[mid] as Candle).t < t) lo = mid + 1
    else hi = mid
  }
  return lo
}

// mergeIntoBucket folds one trade into one candle: h/l widen, c
// becomes the latest print, volume and tx_count accumulate.
function mergeIntoBucket(c: Candle, trade: CandleTrade): Candle {
  // Birth supplies only the opening observation, never activity.
  if (c.provenance === 'pool_birth' && c.tx_count === 0) {
    return firstTradeFromBirth(c, trade, c.t, 0)
  }
  return {
    ...c,
    h: Math.max(c.h, trade.price),
    l: Math.min(c.l, trade.price),
    c: trade.price,
    v: c.v + Math.max(0, trade.quote_amount),
    tx_count: c.tx_count + 1,
  }
}

function firstTradeFromBirth(
  birth: Candle,
  trade: CandleTrade,
  bucket: number,
  step: number
): Candle {
  // The ADD reserve ratio is a real observation. Only the same/adjacent bucket
  // can inherit it; a distant first fill opens at its own factual price.
  const open = bucket >= birth.t && bucket <= birth.t + step ? birth.c : trade.price
  return {
    t: bucket,
    o: open,
    h: Math.max(open, trade.price),
    l: Math.min(open, trade.price),
    c: trade.price,
    v: Math.max(0, trade.quote_amount),
    tx_count: 1,
  }
}

// indexOfBucket binary-searches the (ASC, bucket-aligned) candle array
// for an exact bucket timestamp; -1 when absent.
function indexOfBucket(candles: Candle[], t: number): number {
  let lo = 0
  let hi = candles.length - 1
  while (lo <= hi) {
    const mid = (lo + hi) >> 1
    const mt = (candles[mid] as Candle).t
    if (mt === t) return mid
    if (mt < t) lo = mid + 1
    else hi = mid - 1
  }
  return -1
}

// reanchorClose re-points the CURRENT candle's close at the last known live
// price after a fresh seed. On a timeframe switch the new-resolution seed is the
// MV (which lags writes), and the trades already seen on the prior resolution
// won't be re-delivered over WS — so the current candle would render a stale
// close until the next trade lands. We re-anchor price-only: the bucket keeps the
// seed's volume/tx (they reconcile on the next real merge / reseed), so there is
// no double-count, and every timeframe shows the same live price the instant you
// switch. Returns the SAME array reference when there's nothing to do (empty, no
// live price, or a ts older than the current bucket) so the chart can skip a
// redundant setData. Pure.
export function reanchorClose(
  candles: Candle[],
  price: number | null | undefined,
  ts: number,
  res: Resolution
): Candle[] {
  if (candles.length === 0 || typeof price !== 'number' || !Number.isFinite(price) || price <= 0) {
    return candles
  }
  const bucket = bucketStart(ts, res)
  const last = candles[candles.length - 1] as Candle
  if (bucket < last.t) return candles // stale: older than the current bucket
  const out = candles.slice()
  if (bucket === last.t) {
    out[out.length - 1] = {
      ...last,
      c: price,
      h: Math.max(last.h, price),
      l: Math.min(last.l, price),
    }
    return out
  }
  // bucket > last.t: the seed has not captured the current bucket yet — open it
  // continuous (open at the prior close), volume 0 until the next trade fills it.
  out.push({
    t: bucket,
    o: last.c,
    h: Math.max(last.c, price),
    l: Math.min(last.c, price),
    c: price,
    v: 0,
    tx_count: 0,
  })
  return out
}

// NOTE (CHART-27.2): there is deliberately NO gap-fill here. Earlier
// iterations synthesized flat "continuity" candles (tx_count=0) for
// empty buckets; the founder rejected them — a candle IS a trade, and
// the wils.fun reference renders sparse candles adjacent without any
// synthetic filler. Empty buckets simply don't exist in the series.

// applyContinuousOpen is the read-time CHART-FINAL transform mirrored on the
// client (backend/pkg/api/ohlcv/repository.go::applyContinuousOpen). Each bar's
// open becomes the previous bar's close so a single-trade bucket still draws a
// body and a down-close bar is red; the first bar keeps its own open. high/low
// are re-clamped to enclose the new open so the body never escapes its wick.
//
// The product chart's default newest-N window already arrives continuous from
// the API. Explicit verification ranges may use this helper in isolation, but
// pagination must never apply it across independently fetched chunks: doing so
// would mutate a proven boundary candle and invent a market move across a gap.
// Pure: returns a new array.
export function applyContinuousOpen(candles: Candle[]): Candle[] {
  const out: Candle[] = []
  for (let i = 0; i < candles.length; i++) {
    const c = candles[i] as Candle
    if (i === 0) {
      out.push({ ...c })
      continue
    }
    const open = (out[i - 1] as Candle).c
    out.push({ ...c, o: open, h: Math.max(c.h, open), l: Math.min(c.l, open) })
  }
  return out
}

// prependOlderCandles merges an authoritative older page by exact bucket time.
// Pagination is append-only with respect to the rendered range: an existing
// candle wins every overlap and keeps both its value and object identity. We
// never rewrite open/high/low to bridge a page boundary; that old seam stitch
// turned gaps and distant pool-birth anchors into synthetic vertical wicks.
// The to-only API cursor already returns the page in product candle shape, so
// this function only sorts/dedupes new timestamps (last duplicate wins). A
// standalone zero-activity birth point may be replaced when the page reaches a
// factual candle in that exact bucket.
// Returns the existing array unchanged (same reference) when there is nothing
// older to add, so the caller can detect "no more history" cheaply. Pure.
export function firstFactualCandleTime(candles: readonly Candle[]): number | null {
  for (const candle of candles) {
    if (candle.tx_count > 0) return candle.t
  }
  return null
}

export function prependOlderCandles(older: Candle[], existing: Candle[]): Candle[] {
  if (existing.length === 0) return dedupeCandlesByTime(older)
  const head = existing[0] as Candle
  const anchored = head.provenance === 'pool_birth' && head.tx_count === 0
  const factualIndex = anchored ? existing.findIndex((candle) => candle.tx_count > 0) : 0
  const firstT = factualIndex >= 0 ? (existing[factualIndex] as Candle).t : Number.POSITIVE_INFINITY
  const byTime = new Map<number, Candle>()
  for (const c of older) {
    if (isInsertableOlderCandle(c, head, firstT, anchored)) byTime.set(c.t, c)
  }
  if (byTime.size === 0) return existing
  const anchorReplacement = anchored ? byTime.get(head.t) : undefined
  if (anchorReplacement) byTime.delete(head.t)
  const inserted = Array.from(byTime.values()).sort((a, b) => a.t - b.t)
  const prefix = anchored ? [anchorReplacement ?? head] : []
  const suffix = factualIndex >= 0 ? existing.slice(factualIndex) : existing.slice(1)
  return [...prefix, ...inserted, ...suffix]
}

// stitchPageSeam gives the candle that headed the series before an older page
// was prepended (`seamTime`) the open a reload gives it: the previous candle's
// close (ADR-025 continuity — read-time on the server, the client's own pass in
// USD). A page arrives continuous inside itself, but its first bar keeps its
// own open, and prependOlderCandles keeps the old head as it was; so a paged
// chart drew a bar a reload draws differently (live LOOT 1s, 2026-11-04: 3 of
// 270 candles opened at their own first trade). Like the server, no stitch
// across a known data gap, and none from the zero-activity birth anchor — the
// wicks the old seam stitch drew came from those. Close, volume and the count
// stay; h/l widen to enclose the new open. Returns `candles` when unchanged.
export function stitchPageSeam(
  candles: Candle[],
  seamTime: number,
  gaps: readonly { from: number; to: number }[] = []
): Candle[] {
  const i = indexOfBucket(candles, seamTime)
  if (i <= 0) return candles
  const prev = candles[i - 1] as Candle
  const cur = candles[i] as Candle
  if (prev.tx_count === 0 || cur.tx_count === 0) return candles
  if (gaps.some(({ from, to }) => prev.t < from && cur.t >= to)) return candles
  if (cur.o === prev.c) return candles
  const out = candles.slice()
  out[i] = { ...cur, o: prev.c, h: Math.max(cur.h, prev.c), l: Math.min(cur.l, prev.c) }
  return out
}

function isInsertableOlderCandle(
  candle: Candle,
  head: Candle,
  firstFactualTime: number,
  anchored: boolean
): boolean {
  if (anchored && candle.t === head.t) return candle.tx_count > 0
  return candle.t < firstFactualTime && (!anchored || candle.t > head.t)
}

function dedupeCandlesByTime(candles: readonly Candle[]): Candle[] {
  const byTime = new Map<number, Candle>()
  for (const candle of candles) byTime.set(candle.t, candle)
  return Array.from(byTime.values()).sort((a, b) => a.t - b.t)
}

// mapStable maps `next` through `fn`, reusing the previous output
// element whenever the input element is identity-equal to the previous
// input at the same index. This keeps derived arrays (chart bars,
// volume bars) tail-stable when the source candles are tail-stable —
// which is what lets the chart layer use series.update() instead of a
// full setData() on every live tick.
export function mapStable<A, B>(
  prevIn: readonly A[] | null,
  prevOut: readonly B[] | null,
  next: readonly A[],
  fn: (a: A, i: number) => B
): B[] {
  if (!prevIn || !prevOut || prevIn.length !== prevOut.length) {
    return next.map(fn)
  }
  return next.map((a, i) => (i < prevIn.length && prevIn[i] === a ? (prevOut[i] as B) : fn(a, i)))
}

// tailDelta compares two candle arrays and returns the suffix of `next`
// that changed when `prev` is a same-or-shorter prefix of `next` with
// only the tail mutated/appended (the shape mergeTradeIntoCandles
// produces). Returns null when the change is not tail-only — caller
// must do a full reset (setData) in that case.
export function tailDelta<T>(prev: readonly T[] | null, next: readonly T[]): T[] | null {
  if (!prev) return null
  if (next.length < prev.length || next.length - prev.length > 1) return null
  // Everything except prev's last element must be identity-stable.
  for (let i = 0; i < prev.length - 1; i++) {
    if (prev[i] !== next[i]) return null
  }
  return next.slice(Math.max(0, prev.length - 1))
}

// invertCandle redraws a candle of a pool quoted the other way round: the
// USDT/TON pool's candles are USDT priced in TON, and TON in USD is their
// reciprocal (USDT ≈ $1, ADR-026). 1/x reverses order, so the high is the
// reciprocal of the low and vice versa. Volume (the pool's TON) is unchanged.
// A candle with a non-positive price has no reciprocal: null.
export function invertCandle(c: Candle): Candle | null {
  if (!(c.o > 0 && c.h > 0 && c.l > 0 && c.c > 0)) return null
  return { ...c, o: 1 / c.o, h: 1 / c.l, l: 1 / c.h, c: 1 / c.c }
}
